import { useEffect, useState, type FC } from "react";
import { createMemoryRouter, Navigate, Outlet, RouterProvider, type RouteObject } from "react-router-dom";
import { AuthenticatedUserLayout } from "@/components/AuthenticatedUserLayout";
import { CreditsExhaustionModal } from "@/components/billing/CreditsExhaustionModal";
import { PaymentFailureBanner } from "@/components/billing/PaymentFailureBanner";
import { VerifyCardModal } from "@/components/billing/VerifyCardModal";
import { ExceptionErrorBoundary } from "@/components/ExceptionHandler";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { SettingsPageLayout } from "@/components/settings/SettingsPageLayout";
import { SettingsSidebar } from "@/components/settings/SettingsSidebar";
import { MainContent, NoSidebar, SidebarLayout } from "@/components/SidebarLayout";
import { TierViolationModal } from "@/components/tiering/TierViolationDialog";
import { useEnvironmentSettingsCleanup } from "@/hooks/use-environment-settings-cleanup";
import { useFlagOnaAIEnabled } from "@/hooks/use-flag";
import { useGetEnterpriseBillingInfo } from "@/queries/enterprise-billing-queries";
import { useGitpodCookie } from "@/hooks/use-gitpod-cookie";
import { useLoaded } from "@/hooks/use-loaded";
import { usePerformance } from "@/hooks/use-performance";
import { usePerfInstrumentation, useVscodePerfInstrumentationEnabled } from "@/hooks/use-perf-instrumentation";
import { usePreventBrowserShortcuts } from "@/hooks/use-prevent-browser-shortcuts";
import { useButtonOrAnchorTracking } from "@/hooks/use-segment";
import { useStreamEvents } from "@/hooks/use-stream-events";
import { type MenuItem } from "@/menu-item";
import { getPrincipal, onDidChangePrincipal } from "@/principal";
import { useOrganization } from "@/queries/organization-queries";
import type { RouteHandle } from "@/route-handle";
import { createEnvironmentDetailsRoute } from "@/routes/environments/details/EnvironmentDetailsRoute";
import { LoginPage } from "@/routes/login/LoginPage";
import { OnaOnboardingPage } from "@/routes/ona-onboarding/OnaOnboardingPage";
import { OnaOnboardingSetupPage } from "@/routes/ona-onboarding/OnaOnboardingSetupPage";
import { JOIN_ORGANIZATION_PATH } from "@/routes/join-organization/join-organization";
import { NotFound } from "@/routes/NotFound";
import { projectRoutes } from "@/routes/projects/project-routes";
import { UsagePageRouter } from "@/routes/settings/organization/UsagePageRouter";
import { SettingsRedirect } from "@/routes/settings/SettingsRedirect";
import { SlashRedirect } from "@/routes/SlashRedirect";
import { sentryCreateBrowserRouter } from "@/sentry";
import { OrganizationTier } from "gitpod-next-api/gitpod/v1/organization_pb";

function useHideAgentsPage() {
    const { value: isOnaAIEnabled, loading } = useFlagOnaAIEnabled();
    // Hide when Ona AI is NOT enabled
    return {
        value: !isOnaAIEnabled,
        loading,
    };
}

function useHideUsagePage() {
    const { data: organization } = useOrganization();
    const { data: enterpriseBillingInfo, isLoading: enterpriseLoading } = useGetEnterpriseBillingInfo();
    const tier = organization?.tier;

    const isVisible =
        tier === OrganizationTier.CORE ||
        tier === OrganizationTier.FREE_ONA ||
        (tier === OrganizationTier.ENTERPRISE && enterpriseBillingInfo?.creditUsageEnabled === true);

    return {
        value: !isVisible,
        loading: enterpriseLoading || !tier,
    };
}

export const App: FC = () => {
    useLoaded();
    useButtonOrAnchorTracking();
    useGitpodCookie();
    useEnvironmentSettingsCleanup();
    usePreventBrowserShortcuts(true);

    // VSCode iframe performance instrumentation (localStorage opt-in)
    const vscodePerfEnabled = useVscodePerfInstrumentationEnabled();
    usePerfInstrumentation(vscodePerfEnabled);

    // Initialize app-level performance tracking
    const { markAppReady } = usePerformance({
        component: "App",
    });

    // Mark when React is ready and measure app load time
    useEffect(() => {
        markAppReady();
    }, [markAppReady]);

    // We don't wrap this in an error boundary as the RouterProvider provides its own error boundary which would be triggered
    // before ours. Instead we have a single rootRoute RouteObject which provides the error boundary as the errorElement.
    return (
        <RouterProvider
            router={NewSidebarRouter}
            fallbackElement={
                <div className="flex size-full items-center justify-center">
                    <LoadingState />
                </div>
            }
        />
    );
};

// eslint-disable-next-line react-refresh/only-export-components
export const settingsRoutes: RouteObject = {
    element: <SettingsPageLayout />,
    path: "/settings",
    children: [
        {
            index: true,
            handle: {},
            element: <SettingsRedirect />,
        },
        // Redirects for old user settings URLs — these paths now live in the UserSettingsModal.
        ...["profile", "preferences", "secrets", "git-authentications", "personal-access-tokens", "user-integrations", "experiments"].map(
            (path) => ({
                path,
                lazy: () =>
                    import("@/routes/settings/UserSettingsRedirect").then((m) => ({
                        Component: m.UserSettingsRedirect,
                    })),
            }),
        ),
        {
            path: "manage-organization",
            lazy: () =>
                import("@/routes/settings/organization/manage-organization/ManageOrganizationPage").then((m) => ({
                    Component: m.ManageOrganizationPage,
                })),
            handle: {
                label: "General",
                menuItem: {
                    label: "General",
                    section: "Organization",
                    to: "/settings/manage-organization",
                    isAdminRole: true,
                } satisfies MenuItem,
            },
        },
        {
            path: "announcements",
            lazy: () =>
                import("@/routes/settings/organization/announcements/AnnouncementBannerPage").then((m) => ({
                    Component: m.AnnouncementBannerPage,
                })),
            handle: {
                label: "Announcements",
                menuItem: {
                    label: "Announcements",
                    section: "Organization",
                    to: "/settings/announcements",
                    isAdminRole: true,
                    hiddenOnTiers: [OrganizationTier.FREE, OrganizationTier.FREE_ONA, OrganizationTier.CORE],
                } satisfies MenuItem,
            },
        },
        {
            path: "members",
            handle: {
                label: "Members",
                menuItem: {
                    label: "Members",
                    section: "Organization",
                    to: "/settings/members",
                    isAdminRole: false,
                } satisfies MenuItem,
            },
            children: [
                {
                    index: true,
                    lazy: () =>
                        import("@/routes/settings/organization/members/MembersPage").then((m) => ({
                            Component: m.MembersPage,
                        })),
                },
                {
                    path: "invite",
                    lazy: () =>
                        import("@/routes/settings/organization/members/InviteMembersPage").then((m) => ({
                            Component: m.InviteMembersPage,
                        })),
                    handle: {
                        label: "Invite members",
                    },
                },
                {
                    path: "group/:groupId",
                    lazy: () =>
                        import("@/routes/settings/organization/members/GroupDetailsPage").then((m) => ({
                            Component: m.GroupDetailsPage,
                        })),
                    handle: {
                        label: "Group Details",
                    },
                },
                {
                    path: "service-account/:serviceAccountId",
                    lazy: () =>
                        import("@/routes/settings/organization/members/ServiceAccountDetailsPage").then((m) => ({
                            Component: m.ServiceAccountDetailsPage,
                        })),
                    handle: {
                        label: "Service Account Details",
                    },
                },
            ],
        },
        {
            path: "runners",
            handle: {
                label: "Runners",
                menuItem: {
                    label: "Runners",
                    section: "Organization",
                    to: "/settings/runners",
                    isAdminRole: false,
                } satisfies MenuItem,
            },
            children: [
                {
                    index: true,
                    lazy: () =>
                        import("@/routes/settings/organization/runners/RunnersPage").then((m) => ({
                            Component: m.RunnersPage,
                        })),
                },
                {
                    path: ":runnerId",
                    lazy: () =>
                        import("@/components/runners/details/RunnerDetailsPage").then((m) => ({
                            Component: m.RunnerDetailsPage,
                        })),
                },
            ],
        },
        {
            path: "environments",
            lazy: () =>
                import("@/routes/settings/organization/environment-inventory/EnvironmentInventoryPage").then((m) => ({
                    Component: m.EnvironmentInventoryPage,
                })),
            handle: {
                label: "Environments",
                menuItem: {
                    label: "Environments",
                    section: "Organization",
                    to: "/settings/environments",
                    isAdminRole: true,
                } satisfies MenuItem,
            },
        },
        {
            path: "organization-secrets",
            lazy: () =>
                import("@/routes/settings/organization/secrets/OrganizationSecretsPage").then((m) => ({
                    Component: m.OrganizationSecretsPage,
                })),
            handle: {
                label: "Secrets",
                menuItem: {
                    label: "Secrets",
                    section: "Organization",
                    to: "/settings/organization-secrets",
                    isAdminRole: true,
                } satisfies MenuItem,
            },
        },
        {
            path: "agents",
            lazy: () =>
                import("@/routes/settings/organization/agents/AgentsPage").then((m) => ({
                    Component: m.AgentsPage,
                })),
            handle: {
                label: "Agents",
                menuItem: {
                    label: "Agents",
                    section: "Organization",
                    to: "/settings/agents",
                    isAdminRole: true,
                    hidden: useHideAgentsPage,
                } satisfies MenuItem,
            },
        },
        {
            path: "org-integrations",
            lazy: () =>
                import("@/routes/settings/organization/integrations/IntegrationsPage").then((m) => ({
                    Component: m.OrgIntegrationsPage,
                })),
            handle: {
                label: "Integrations",
                menuItem: {
                    label: "Integrations",
                    section: "Organization",
                    to: "/settings/org-integrations",
                    isAdminRole: true,
                    hidden: useHideAgentsPage,
                } satisfies MenuItem,
            },
        },
        {
            path: "policies",
            lazy: () =>
                import("@/routes/settings/organization/policies/OrganizationPoliciesPage").then((m) => ({
                    Component: m.OrganizationPoliciesPage,
                })),
            handle: {
                label: "Policies",
                menuItem: {
                    label: "Policies",
                    section: "Organization",
                    to: "/settings/policies",
                    isAdminRole: true,
                } satisfies MenuItem,
            },
        },
        {
            path: "login",
            lazy: () =>
                import("@/routes/settings/login/LoginAndSecurityPage").then((m) => ({
                    Component: m.LoginAndSecurityPage,
                })),
            handle: {
                label: "Login and security",
                menuItem: {
                    label: "Login and security",
                    section: "Organization",
                    to: "/settings/login",
                    isAdminRole: true,
                } satisfies MenuItem,
            },
        },
        {
            path: "billing",
            lazy: () =>
                import("@/routes/settings/organization/billing/BillingPage").then((m) => ({
                    Component: m.default,
                })),
            handle: {
                label: "Billing",
                menuItem: {
                    label: "Billing",
                    section: "Organization",
                    to: "/settings/billing",
                    isAdminRole: true,
                    hiddenOnTiers: [OrganizationTier.FREE, OrganizationTier.ENTERPRISE],
                } satisfies MenuItem,
            },
        },
        {
            path: "credit-usage",
            element: <UsagePageRouter />,
            handle: {
                label: "Usage",
                menuItem: {
                    label: "Usage",
                    section: "Organization",
                    to: "/settings/credit-usage",
                    isAdminRole: true,
                    hiddenOnTiers: [OrganizationTier.FREE],
                    hidden: useHideUsagePage,
                } satisfies MenuItem,
            },
        },
    ],
};

// eslint-disable-next-line react-refresh/only-export-components
export const legacyOnboardingRoutes: RouteObject = {
    path: "/onboarding",
    children: [
        {
            path: "/onboarding/primary-interests",
            element: <Navigate to="/" replace />,
        },
        {
            path: "how-gitpod-works",
            element: <Navigate to="/" replace />,
        },
        {
            path: "set-up-runner/*",
            element: <Navigate to="/" replace />,
        },
        {
            path: "automate-your-dev-environment",
            element: <Navigate to="/" replace />,
        },
        {
            path: "share-with-your-team",
            element: <Navigate to="/" replace />,
        },
    ],
};

// eslint-disable-next-line react-refresh/only-export-components
export const onaOnboardingRoutes: RouteObject = {
    element: <AuthenticatedUserLayout />,
    children: [
        {
            path: "/onboarding/ona",
            element: <OnaOnboardingSetupPage />,
        },
        {
            path: "/onboarding/ona/connect-to-github",
            element: <OnaOnboardingPage step="connect-to-github" />,
        },
        {
            path: "/onboarding/ona/select-your-repository",
            element: <OnaOnboardingPage step="select-your-repository" />,
        },
        {
            path: "/onboarding/ona/get-started-with-tasks",
            element: <OnaOnboardingPage step="get-started-with-tasks" />,
        },
    ],
};

// eslint-disable-next-line react-refresh/only-export-components
export const appRoutes: RouteObject[] = [
    onaOnboardingRoutes,
    {
        // Pages using Sidebar
        element: <SidebarLayout />,
        children: [
            {
                element: <AuthenticatedUserLayout />,
                children: [
                    {
                        index: true,
                        element: <SlashRedirect />,
                    },
                    projectRoutes,
                    {
                        path: "ai",
                        children: [
                            {
                                index: true,
                                lazy: () =>
                                    import("@/routes/ai/OnaAIPage").then((m) => ({
                                        Component: m.OnaAIPage,
                                    })),
                            },
                        ],
                    },
                    {
                        path: "insights",
                        lazy: () =>
                            import("@/routes/insights/InsightsPage").then((m) => ({
                                Component: m.InsightsPage,
                            })),
                    },
                    {
                        path: "automations",
                        lazy: () =>
                            import("@/routes/automations/AutomationsLayout").then((m) => ({
                                Component: m.AutomationsLayout,
                            })),
                        children: [
                            {
                                lazy: () =>
                                    import("@/routes/automations/AutomationsPage").then((m) => ({
                                        Component: m.AutomationsPage,
                                    })),
                                handle: {
                                    layout: {
                                        noPadding: true,
                                    },
                                } satisfies RouteHandle,
                                children: [
                                    {
                                        index: true,
                                        element: null,
                                    },
                                    {
                                        path: "webhooks",
                                        lazy: () =>
                                            Promise.all([
                                                import("@/routes/automations/RequireWebhooksCapability"),
                                                import("@/components/webhooks/WebhooksSidePanel"),
                                            ]).then(([guard, panel]) => ({
                                                Component: () => (
                                                    <guard.RequireWebhooksCapability>
                                                        <panel.WebhooksSidePanel />
                                                    </guard.RequireWebhooksCapability>
                                                ),
                                            })),
                                        handle: {
                                            layout: {
                                                scrollable: false,
                                            },
                                        } satisfies RouteHandle,
                                        children: [
                                            {
                                                path: ":webhookId",
                                                lazy: () =>
                                                    import("@/routes/automations/WebhookDetailsPage").then((m) => ({
                                                        Component: m.WebhookDetailsPage,
                                                    })),
                                            },
                                        ],
                                    },
                                ],
                            },
                            {
                                path: "new",
                                lazy: () =>
                                    Promise.all([
                                        import("@/routes/automations/RequireFullAutomationAccess"),
                                        import("@/routes/automations/NewAutomationPage"),
                                    ]).then(([guard, page]) => ({
                                        Component: () => (
                                            <guard.RequireFullAutomationAccess>
                                                <page.NewAutomationPage />
                                            </guard.RequireFullAutomationAccess>
                                        ),
                                    })),
                                handle: {
                                    layout: {
                                        noPadding: true,
                                    },
                                } satisfies RouteHandle,
                            },
                            {
                                path: ":workflowId",
                                lazy: () =>
                                    Promise.all([
                                        import("@/routes/automations/RequireFullAutomationAccess"),
                                        import("@/routes/automations/automation-details-page/AutomationDetailsPage"),
                                    ]).then(([guard, page]) => ({
                                        Component: () => (
                                            <guard.RequireFullAutomationAccess>
                                                <page.AutomationDetailsPage />
                                            </guard.RequireFullAutomationAccess>
                                        ),
                                    })),
                                handle: {
                                    layout: {
                                        noPadding: true,
                                        scrollable: false,
                                    },
                                } satisfies RouteHandle,
                            },
                            {
                                path: ":workflowId/edit",
                                lazy: () =>
                                    Promise.all([
                                        import("@/routes/automations/RequireFullAutomationAccess"),
                                        import("@/routes/automations/automation-edit-page/AutomationEditPage"),
                                    ]).then(([guard, page]) => ({
                                        Component: () => (
                                            <guard.RequireFullAutomationAccess>
                                                <page.AutomationEditPage />
                                            </guard.RequireFullAutomationAccess>
                                        ),
                                    })),
                                handle: {
                                    layout: {
                                        noPadding: true,
                                    },
                                } satisfies RouteHandle,
                            },
                            {
                                path: "executions/:workflowExecutionId",
                                lazy: () =>
                                    Promise.all([
                                        import("@/routes/automations/RequireFullAutomationAccess"),
                                        import("@/routes/automations/AutomationExecutionLayout"),
                                    ]).then(([guard, layout]) => ({
                                        Component: () => (
                                            <guard.RequireFullAutomationAccess>
                                                <layout.AutomationExecutionLayout />
                                            </guard.RequireFullAutomationAccess>
                                        ),
                                    })),
                                handle: {
                                    layout: {
                                        noPadding: true,
                                    },
                                } satisfies RouteHandle,
                                children: [
                                    {
                                        index: true,
                                        lazy: () =>
                                            import("@/routes/automations/AutomationExecutionSummaryPage").then((m) => ({
                                                Component: m.AutomationExecutionSummaryPage,
                                            })),
                                    },
                                    {
                                        path: "actions/:workflowExecutionActionId",
                                        lazy: () =>
                                            import("@/routes/automations/AutomationExecutionActionPage").then((m) => ({
                                                Component: m.AutomationExecutionActionPage,
                                            })),
                                    },
                                ],
                            },
                        ],
                    },
                    legacyOnboardingRoutes,
                    {
                        path: "create",
                        element: <Navigate to="/" replace state={{ showCreateModal: true }} />,
                    },
                ],
            },
        ],
    },
    // Environment details routes uses a custom content area
    {
        element: <SidebarLayout CustomContent={Outlet} />,
        children: [
            {
                element: (
                    <AuthenticatedUserLayout
                        // Wrapping the LoadingState in MainContent so that we render a full blank page
                        // while the page is loading. This is the default behavior of SidebarLayout if you aren't
                        // passing in CustomContent, so this just ensures that we have a consistent loading
                        // state.
                        LoadingComponent={() => (
                            <MainContent>
                                <LoadingState />
                            </MainContent>
                        )}
                    />
                ),
                children: [createEnvironmentDetailsRoute()],
            },
        ],
    },
    // Settings pages with custom sidebar
    {
        element: <SidebarLayout CustomSidebar={SettingsSidebar} disableCollapse />,
        children: [
            {
                element: <AuthenticatedUserLayout />,
                children: [settingsRoutes],
            },
        ],
    },

    {
        element: <AuthenticatedUserLayout />,
        children: [
            {
                path: "/create-organization",
                lazy: () =>
                    import("@/routes/create-organization/CreateOrganizationPage").then((m) => ({
                        Component: m.CreateOrganizationPage,
                    })),
            },
            {
                path: "/integrations/connect/:integrationId",
                lazy: () =>
                    import("@/routes/integrations/ConnectIntegrationPage").then((m) => ({
                        Component: m.ConnectIntegrationPage,
                    })),
            },
            {
                path: "/integrations/connected/:integrationId",
                lazy: () =>
                    import("@/routes/integrations/IntegrationConnectedPage").then((m) => ({
                        Component: m.IntegrationConnectedPage,
                    })),
            },
        ],
    },

    // Experiments activation route - no sidebar, authenticated
    {
        element: <SidebarLayout CustomSidebar={NoSidebar} disableCollapse disableMobileMenu />,
        children: [
            {
                element: <AuthenticatedUserLayout />,
                children: [
                    {
                        path: "/experiments/:name",
                        lazy: () =>
                            import("@/routes/experiments/ExperimentsActivationPage").then((m) => ({
                                Component: m.ExperimentsActivationPage,
                            })),
                    },
                ],
            },
        ],
    },

    // Pages without a Sidebar
    {
        element: <SidebarLayout CustomSidebar={NoSidebar} disableCollapse disableMobileMenu />,
        children: [
            {
                path: "/",
                element: <AuthenticatedUserLayout />,
                children: [
                    {
                        path: `${JOIN_ORGANIZATION_PATH}/:inviteId?`,
                        lazy: () =>
                            import("@/routes/join-organization/JoinOrganizationPage").then((m) => ({
                                Component: m.JoinOrganizationPage,
                            })),
                    },
                ],
            },
        ],
    },
    // UNAUTHENTICATED ROUTES
    {
        path: "/login/ona",
        element: <Navigate to="/login" replace />,
    },
    {
        path: "/login/ona/new",
        element: <Navigate to="/login" replace />,
    },
    {
        children: [
            {
                path: "/login",
                element: <LoginPage onaLayout />,
            },
        ],
    },
    // Pages without a Sidebar
    {
        element: <SidebarLayout CustomSidebar={NoSidebar} />,
        children: [
            {
                path: "/magiclink",
                lazy: () =>
                    import("@/routes/magiclink/MagiclinkPage").then((m) => ({
                        Component: m.MagiclinkPage,
                    })),
            },
            {
                path: "/confirm/signin",
                lazy: () =>
                    import("@/routes/signin-desktop/SigninDesktopPage").then((m) => ({
                        Component: m.SigninDesktopPage,
                    })),
            },
            {
                path: "/local-runner",
                lazy: () =>
                    import("@/routes/local-runner/LocalRunner").then((m) => ({
                        Component: m.LocalRunner,
                    })),
            },
            {
                path: "/quit-app",
                lazy: () =>
                    import("@/routes/local-runner/LocalRunner").then((m) => ({
                        Component: m.QuitApp,
                    })),
            },
            {
                path: "/account-deleted",
                lazy: () =>
                    import("@/routes/acconut-deleted/AccountDeletedPage").then((m) => ({
                        Component: m.AccountDeletedPage,
                    })),
            },
            {
                path: "*",
                element: <NotFound />,
            },
        ],
    },

    // Standalone pages without any layout wrapper
    {
        path: "/runner/scm-success",
        lazy: () =>
            import("@/routes/runner/ScmSuccessPage").then((m) => ({
                Component: m.ScmSuccessPage,
            })),
    },

    {
        children: [
            {
                path: "/details/:environmentId/code-changes",
                lazy: () =>
                    import("@/routes/environments/code-changes/EnvironmentCodeChanges").then((m) => ({
                        Component: m.EnvironmentCodeChanges,
                    })),
            },
        ],
    },
];

/**
 * This component ensures that we watch for org-level events.
 *
 * This is a temporary solution until we can rework our route tree so that we only have one instantiation of AuthenticatedUserLayout
 * which would then be responsible for watching for org-level events.
 *
 * See AGENT-477
 */
const StreamOrgEvents = () => {
    // This aborts early if there there isn't a current user, so it's safe to call even on routes that render without an authenticated user
    const { unsubscribe } = useStreamEvents({ case: "organization", value: true });

    // This ensures that we unsubscribe/resubscribe to the event stream when the principal changes (the user changes org)
    const [, setPrincipal] = useState<string | null>(null);
    useEffect(() => {
        return onDidChangePrincipal(() => {
            unsubscribe(); // Unsubscribe from the previous event stream
            // This causes a re-render so we resubscribe to the event stream (with the updated principal)
            setPrincipal(getPrincipal());
        });
    }, [setPrincipal, unsubscribe]);

    return null;
};

/**
 * The primary purpose of the root route is to wrap the routes in an error boundary which
 * triggers instead of the default error boundary of the RouterProvider.
 */
const rootRoute = {
    element: (
        <ExceptionErrorBoundary>
            <PaymentFailureBanner />
            <TierViolationModal />
            <CreditsExhaustionModal />
            <VerifyCardModal />
            <StreamOrgEvents />
            <Outlet />
        </ExceptionErrorBoundary>
    ),
    children: appRoutes,
} satisfies RouteObject;

export const NewSidebarRouter = sentryCreateBrowserRouter([rootRoute]);
export const CreateTestRouter = (opts?: { initialEntries?: string[] }) => createMemoryRouter([rootRoute], opts);
